import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import type { TableColumnDef } from '../../hooks/usePersistedTableColumns';

export interface TableColumnChooserProps {
  /** Every column the chooser may offer, in the user's current order (hidden ones included). */
  columns: TableColumnDef[];
  isVisible: (field: string) => boolean;
  setVisible: (field: string, visible: boolean) => void;
  /** Receives the full ordered field list after a move. */
  setOrder: (fields: string[]) => void;
  resetToDefault: () => void;
  /** Button label — sentence case. */
  label?: string;
  /**
   * The saved-view read failed, so these are the default columns rather than
   * the user's own (PRX-80 final review, item 7). The chooser says so in one
   * quiet line — no toast, nothing to dismiss: a preferences outage must not
   * interrupt the work, but it must not pass for "you never saved a view".
   */
  loadFailed?: boolean;
}

/**
 * A `Columns` button opening a popover with one checkbox per column, move
 * up / move down controls to reorder (keyboard-reachable; no drag library is
 * added), and `Reset to default` (PRX-80 Task 14). `alwaysVisible` columns
 * render a disabled, checked box.
 *
 * PRX-137: rows can also be dragged with native HTML5 drag-and-drop. A drop
 * hands `setOrder` the same full field list a move button does, so the order
 * persists the same way; the move buttons stay for keyboard users.
 */
export const TableColumnChooser: React.FC<TableColumnChooserProps> = ({
  columns,
  isVisible,
  setVisible,
  setOrder,
  resetToDefault,
  label = 'Columns',
  loadFailed = false,
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = Boolean(anchor);

  const move = (index: number, delta: -1 | 1) => {
    const target = index + delta;
    if (target < 0 || target >= columns.length) return;
    const fields = columns.map((c) => c.field);
    [fields[index], fields[target]] = [fields[target], fields[index]];
    setOrder(fields);
  };

  // The row being dragged. Held here rather than read back from dataTransfer,
  // so a drop that did not start in this list (a file, another list) is ignored.
  const dragging = useRef<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  // A row dragged up lands before its target, one dragged down lands after it
  // (the splice in `dropOn`). The indicator is drawn on that side (review L5).
  const dropSide = (targetField: string): 'before' | 'after' | undefined => {
    if (dropTarget !== targetField || !dragging.current) return undefined;
    const fields = columns.map((c) => c.field);
    return fields.indexOf(dragging.current) < fields.indexOf(targetField) ? 'after' : 'before';
  };

  const dropOn = (targetField: string) => {
    const source = dragging.current;
    dragging.current = null;
    setDropTarget(null);
    if (!source || source === targetField) return;
    const fields = columns.map((c) => c.field);
    const from = fields.indexOf(source);
    const to = fields.indexOf(targetField);
    if (from < 0 || to < 0) return;
    fields.splice(from, 1);
    fields.splice(to, 0, source);
    setOrder(fields);
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<ViewColumnIcon fontSize="small" />}
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        {label}
      </Button>
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: { p: 1.5, minWidth: 280 },
            ...({ role: 'dialog', 'aria-label': 'Choose columns' } as React.HTMLAttributes<HTMLDivElement>),
          },
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
          <Typography variant="subtitle2">Columns</Typography>
          <Button size="small" onClick={resetToDefault}>
            Reset to default
          </Button>
        </Stack>
        {loadFailed && (
          <Typography variant="body2" color="text.secondary" role="status" sx={{ mb: 1 }}>
            Your saved columns could not be loaded, so the table is showing the default columns.
          </Typography>
        )}
        <List dense disablePadding>
          {columns.map((column, index) => (
            <ListItem
              key={column.field}
              disableGutters
              draggable
              data-testid={`column-chooser-row-${column.field}`}
              data-drop-position={dropSide(column.field)}
              onDragStart={(e) => {
                dragging.current = column.field;
                e.dataTransfer.effectAllowed = 'move';
                // Firefox starts no drag without data.
                e.dataTransfer.setData('text/plain', column.field);
              }}
              onDragOver={(e) => {
                if (!dragging.current) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                if (dropTarget !== column.field) setDropTarget(column.field);
              }}
              onDragLeave={() => {
                if (dropTarget === column.field) setDropTarget(null);
              }}
              onDrop={(e) => {
                e.preventDefault();
                dropOn(column.field);
              }}
              onDragEnd={() => {
                dragging.current = null;
                setDropTarget(null);
              }}
              sx={{
                cursor: 'grab',
                borderTop: '2px solid',
                borderBottom: '2px solid',
                borderTopColor: dropSide(column.field) === 'before' ? 'primary.main' : 'transparent',
                borderBottomColor: dropSide(column.field) === 'after' ? 'primary.main' : 'transparent',
              }}
              secondaryAction={
                <Box>
                  <IconButton
                    size="small"
                    aria-label={`Move ${column.label} up`}
                    disabled={index === 0}
                    onClick={() => move(index, -1)}
                  >
                    <ArrowUpwardIcon fontSize="inherit" />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label={`Move ${column.label} down`}
                    disabled={index === columns.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    <ArrowDownwardIcon fontSize="inherit" />
                  </IconButton>
                </Box>
              }
            >
              <DragIndicatorIcon fontSize="small" color="action" aria-hidden="true" sx={{ mr: 0.5 }} />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={isVisible(column.field)}
                    disabled={Boolean(column.alwaysVisible)}
                    onChange={(e) => setVisible(column.field, e.target.checked)}
                    inputProps={{ 'aria-label': column.label }}
                  />
                }
                label={column.label}
                title={column.alwaysVisible ? 'This column is always shown' : undefined}
              />
            </ListItem>
          ))}
        </List>
      </Popover>
    </>
  );
};

export default TableColumnChooser;
